import PreparationType, { preparationTypes } from './PreparationType'
import Table from '../ui/table/Table'

// Dayjs
import dayjs from 'dayjs'

// Mantine
import { Anchor, Tooltip, Popover } from '@mantine/core'

// React Router
// Classnames
import classNames from 'classnames'

import { IconAlertOctagon, IconChevronDown } from '@tabler/icons-react'

// UI
import ShopifyProductMultiSelect from '../ui/ShopifyProductMultiSelect'
import ComponentMultiSelect from '../ui/ComponentMultiSelect'

// Cells
const NameCell = ({ refetchData, ...item }) => {
    return (
        <div className="table-cell min-w-[200px] whitespace-nowrap p-5">
            <div className="flex flex-col items-start">
                <div className="flex gap-1 items-center">
                    <Anchor
                        href={'/fulfillment_orders/' + item.id}
                    >
                        BP{item.id}
                    </Anchor>

                    {item.verify_address_error && (
                        <Tooltip
                            multiline
                            w={220}
                            label={item.verify_address_error}
                        >
                            <IconAlertOctagon size={15} color="red" />
                        </Tooltip>
                    )}
                </div>

                <span className="text-gray-500 text-sm">
                    {dayjs(item.created_at).format('ll - LT')}
                </span>
            </div>
        </div>
    )
}

const ConsigneCell = (item) => {
    const { recipient } = item
    const name = recipient?.name || 'Pas de destinataire'
    const destination = [recipient?.city, recipient?.country_code || recipient?.country].filter(Boolean).join(', ')
    return (
        <div className="table-cell px-5 py-3 text-sm">
            <div className="w-64">
                <div className="flex items-center gap-2">
                    <PreparationType order={item} />
                    <span className="min-w-0 truncate" title={name}>{name}</span>
                </div>
                <div className="mt-0.5 truncate text-gray-500" title={destination}>{destination}</div>
            </div>
        </div>
    )
}

const ProductLine = ({ line }) => {
    const variant = line.shopify_product_variant
    const name = variant?.shopify_product?.title || line.component?.name || line.name || 'Article non disponible'
    const details = variant
        ? (variant.title === 'Default Title' ? null : variant.title)
        : line.component?.component_category?.name_fr

    return (
        <div className="flex items-start gap-3 text-sm">
            <span className="shrink-0 text-gray-500 tabular-nums">×{line.remaining_quantity}</span>
            <div className="min-w-0 flex-1">
                <div className="truncate" title={name}>{name}</div>
                {details && <div className="mt-0.5 truncate text-gray-500" title={details}>{details}</div>}
            </div>
        </div>
    )
}

const ProductsCell = ({ lines = [] }) => {
    const remainingLines = lines.filter((line) => Number(line.remaining_quantity) > 0)
    const collapsed = remainingLines.length > 3
    const quantities = remainingLines.reduce((totals, line) => {
        const type = line.variant_id ? 'variants' : line.component_id ? 'components' : 'other'
        totals[type] += Number(line.remaining_quantity)
        return totals
    }, { variants: 0, components: 0, other: 0 })
    const summary = [
        quantities.variants > 0 && `${quantities.variants} variante${quantities.variants > 1 ? 's' : ''}`,
        quantities.components > 0 && `${quantities.components} composant${quantities.components > 1 ? 's' : ''}`,
        quantities.other > 0 && `${quantities.other} article${quantities.other > 1 ? 's' : ''} non identifié${quantities.other > 1 ? 's' : ''}`,
    ].filter(Boolean).join(' · ')

    return (
        <div className="table-cell px-5 py-3 text-sm">
            <div className="w-[360px] xl:w-[420px]">
                {remainingLines.length === 0 && <span className="text-gray-500">Aucun produit</span>}
                {!collapsed && remainingLines.length > 0 && (
                    <div className="space-y-3">
                        {remainingLines.map((line) => <ProductLine key={line.id} line={line} />)}
                    </div>
                )}
                {collapsed && <Popover
                    position="bottom-start" width={560} shadow="md" radius="md" withinPortal returnFocus
                    middlewares={{
                        flip: { padding: 12 },
                        shift: { padding: 12 },
                        size: {
                            padding: 12,
                            apply: ({ availableWidth, availableHeight, elements }) => {
                                Object.assign(elements.floating.style, {
                                    maxWidth: `${Math.max(0, availableWidth)}px`,
                                    maxHeight: `${Math.max(0, Math.min(480, availableHeight))}px`,
                                })
                            },
                        },
                    }}
                >
                    <Popover.Target>
                        <button type="button" className="flex min-h-11 items-center gap-2 whitespace-nowrap rounded py-1 text-sm hover:underline focus-visible:outline-2 focus-visible:outline-baltic">
                            {summary}
                            <IconChevronDown size={15} aria-hidden="true" />
                        </button>
                    </Popover.Target>
                    <Popover.Dropdown
                        style={{ maxWidth: 'calc(100dvw - 24px)', maxHeight: 'min(480px, calc(100dvh - 24px))', overflowY: 'auto', overscrollBehavior: 'contain' }}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="space-y-3">
                            {remainingLines.map((line) => <ProductLine key={line.id} line={line} />)}
                        </div>
                    </Popover.Dropdown>
                </Popover>}
            </div>
        </div>
    )
}

const FulfillAtCell = ({ refetchData, ...item }) => {
    const isTodayOrBefore =
        item.fulfill_at &&
        dayjs(item.fulfill_at).format('YYYY-MM-DD') <=
            dayjs().format('YYYY-MM-DD')

    const color = classNames({
        'text-green-500': isTodayOrBefore,
        'text-red-500': !isTodayOrBefore,
    })

    return (
        <div className="table-cell">
            <div className="px-5 py-3 flex whitespace-nowrap text-sm">
                <span className={color}>
                    {item.fulfill_at && dayjs(item.fulfill_at).format('ll')}
                </span>

                {!item.fulfill_at && (
                    <span className="opacity-50">Pas de date</span>
                )}
            </div>
        </div>
    )
}

const LocationCell = ({ refetchData, ...item }) => {
    return (
        <div className="table-cell whitespace-nowrap p-5">
            <div className="flex flex-col items-start">
                <span className="text-sm opacity-50">
                    {item.location?.name}
                </span>
            </div>
        </div>
    )
}

// Component
export default function FulfillmentOrdersTable() {
    const availableColumns = [
        {
            name: 'Demande',
            key: 'id',
            display: 'mandatory',
            sortable: true,
            component: NameCell,
        },
        {
            name: 'Destinataire',
            key: 'consignee',
            display: 'optional_default',
            component: ConsigneCell,
        },
        {
            name: 'Produits',
            key: 'products',
            display: 'optional_default',
            component: ProductsCell,
        },
        {
            name: 'Date de préparation',
            key: 'fulfill_at',
            display: 'optional_default',
            sortable: true,
            component: FulfillAtCell,
        },
        {
            name: 'Lieu',
            key: 'location',
            display: 'optional',
            component: LocationCell,
        },
    ]

    const WithShopifyVariantsFilter = ({ value, onChange }) => (
        <ShopifyProductMultiSelect
            value={value}
            onChange={onChange}
            label="Incluant la variante"
        />
    )

    const WithoutShopifyVariantsFilter = ({ value, onChange }) => (
        <ShopifyProductMultiSelect
            value={value}
            onChange={onChange}
            label="Excluant la variante"
        />
    )

    const WithComponentsFilter = ({ value, onChange }) => (
        <ComponentMultiSelect
            value={value}
            onChange={onChange}
            label="Incluant le composant"
            placeholder="Sélectionner des composants"
        />
    )

    const WithoutComponentsFilter = ({ value, onChange }) => (
        <ComponentMultiSelect
            value={value}
            onChange={onChange}
            label="Excluant le composant"
            placeholder="Sélectionner des composants"
        />
    )

    const typeOptions = Object.entries(preparationTypes).map(([value, definition]) => ({
        value,
        label: definition.filterLabel,
    }))

    const availableFilters = [
        {
            name: 'Date de préparation',
            key: 'fulfill_at',
            type: 'select',
            options: [
                { value: 'today_or_before', label: "Aujourd'hui ou avant" },
                { value: '3_days_or_before', label: 'J+3 ou avant' },
                { value: '7_days_or_before', label: 'J+7 ou avant' },
                { value: 'all', label: 'Toutes dates' },
            ],
        },
        {
            name: 'Incluant la variante',
            key: 'with_shopify_variants',
            type: 'custom',
            multiple: true,
            component: WithShopifyVariantsFilter,
        },
        {
            name: 'Excluant la variante',
            key: 'without_shopify_variants',
            type: 'custom',
            multiple: true,
            component: WithoutShopifyVariantsFilter,
        },
        {
            name: 'Type de demande',
            key: 'with_types',
            type: 'multiselect',
            placeholder: 'Inclure ces types',
            options: typeOptions,
        },
        {
            name: 'Type de demande exclu',
            key: 'without_types',
            type: 'multiselect',
            placeholder: 'Exclure ces types',
            options: typeOptions,
        },
        {
            name: 'Incluant le composant',
            key: 'with_components',
            type: 'custom',
            multiple: true,
            component: WithComponentsFilter,
        },
        {
            name: 'Excluant le composant',
            key: 'without_components',
            type: 'custom',
            multiple: true,
            component: WithoutComponentsFilter,
        },
        {
            name: 'Type de préparation',
            key: 'quantities',
            type: 'select',
            options: [
                { value: 'single', label: 'Mono-produit' },
                { value: 'multiple', label: 'Multi-produits' },
            ],
        },
        {
            name: 'Erreur adresse DHL',
            key: 'verify_address_error',
            type: 'select',
            options: [
                { value: 'true', label: "Avec erreurs d'adresse" },
            ],
        },
        // Ces préparations sont retirées de la file par défaut : le client a
        // demandé l'annulation et personne n'a encore tranché. Ce filtre est le
        // seul endroit d'où les retrouver.
        {
            name: "Demande d'annulation",
            key: 'cancellation_requested',
            type: 'select',
            options: [
                { value: 'true', label: "Annulation demandée" },
            ],
        },
    ]

    return (
        <Table
            availableColumns={availableColumns}
            availableFilters={availableFilters}
            engineApiEndpoint="fulfillment-orders/table"
            defaultSortColumn="fulfill_at"
            defaultSortDirection="asc"
            linkItemUrl="/fulfillment_orders"
            linkItemId="id"
            withLocation={true}
        />
    )
}
